import { Link } from "react-router-dom";
import GuidePage from "@/components/marketing/GuidePage";
import { BRAND } from "@/content/brand";
import { Table } from "./BuyingGuides";

const UPDATED = "2026-09-24";

/* ------------------------------------------------------------------ */
/* Hair                                                                */
/* ------------------------------------------------------------------ */

export function ForHair() {
  return (
    <GuidePage
      path="/coconut-oil-for-hair"
      seoTitle="Coconut Oil for Hair: How to Do Champi"
      seoDescription="How to use coconut oil for hair: warm it, how much to use for your hair length, how long to leave it, how to wash it out, and what the research shows."
      eyebrow="Hair guide"
      title="Coconut oil for hair: how to do a proper champi"
      tldr="Warm a small amount of coconut oil, massage it into your scalp and through the lengths, leave it for at least an hour or overnight, then wash with a mild shampoo. In lab studies on hair fibres, coconut oil used before washing reduced protein loss (Rele and Mohile, 2003). There is no good evidence that it stops hair fall or cures dandruff, and we will not claim it does."
      updated={UPDATED}
      testid="guide-hair"
      sections={[
        {
          id: "champi",
          heading: "Why do Indian families oil their hair?",
          body: (
            <>
              <p>
                Most of us remember it: sitting on the floor on a Sunday morning while an amma, ajji or older sister worked warm coconut oil into our scalp. Champi is one of the oldest habits in Indian homes, especially in the South and along the coast, where coconut oil has always been the everyday hair oil.
              </p>
              <p>
                People keep doing it because hair feels softer, easier to comb and less dry after a wash. The massage itself is part of the ritual. It is a slow, calm half hour, and many people simply like how it feels.
              </p>
            </>
          ),
        },
        {
          id: "how-to",
          heading: "How to oil your hair with coconut oil, step by step",
          body: (
            <>
              <ol>
                <li><strong>Warm the oil gently.</strong> Stand the jar in a bowl of warm water for a few minutes, or warm a small katori of oil over another bowl of hot water. It should feel pleasantly warm on your wrist, never hot. If your oil has turned solid in the cold, this melts it too.</li>
                <li><strong>Part your hair.</strong> Work in sections so the oil reaches the scalp, not just the top layer of hair.</li>
                <li><strong>Massage the scalp.</strong> Dip your fingertips in the oil and move them in small circles for five to ten minutes. Use the pads of your fingers, not your nails.</li>
                <li><strong>Spread it through the lengths.</strong> Run what is left on your palms down to the ends, which are usually the driest part.</li>
                <li><strong>Wait.</strong> Leave it in for at least an hour. Many people leave it overnight with an old towel on the pillow.</li>
                <li><strong>Wash.</strong> Shampoo once, rinse, and shampoo again if your hair still feels greasy. Use lukewarm water.</li>
              </ol>
            </>
          ),
        },
        {
          id: "how-much",
          heading: "How much coconut oil should you use?",
          body: (
            <>
              <p>
                Less than you think. Too much oil is hard to wash out and leaves hair limp. Start with the amount below and adjust.
              </p>
              <Table
                caption="How much coconut oil to use by hair length"
                head={["Hair length", "Amount to start with", "Tip"]}
                rows={[
                  ["Short (above the ears)", "1 to 2 teaspoons", "Focus on the scalp."],
                  ["Chin to shoulder length", "2 to 3 teaspoons", "Oil the scalp first, then the ends."],
                  ["Below the shoulders", "1 tablespoon", "Add a little more to the ends if they are dry."],
                  ["Waist length or very thick", "1 to 2 tablespoons", "Work in sections so the scalp is not missed."],
                  ["Children", "Half a teaspoon to 1 teaspoon", "Keep it light and away from the eyes."],
                ]}
              />
              <p>
                Once or twice a week is how most families do it. If your scalp tends to feel oily, once a week may be enough.
              </p>
            </>
          ),
        },
        {
          id: "research",
          heading: "What does the research on coconut oil and hair actually show?",
          body: (
            <>
              <p>
                The study you will see quoted most often is by Rele and Mohile, published in the Journal of Cosmetic Science in 2003. They compared mineral oil, sunflower oil and coconut oil on hair samples. Of the three, only coconut oil significantly reduced protein loss, in both undamaged and damaged hair, when used before and after washing.
              </p>
              <p>
                It is a useful study, but know its limits. It was done in a lab on hair fibres, not on people's heads, and it measured protein loss, not hair growth or hair fall. The authors also worked at the research and development unit of Marico, which makes coconut hair oil, so there is an industry link.
              </p>
              <p>
                An earlier lab study (Ruetsch and colleagues, 2001) used an imaging method to show that coconut oil gets into the hair shaft, while mineral oil does not. Together, these studies suggest a reason oiled hair can feel different after a wash. They do not show that coconut oil treats any scalp or hair condition.
              </p>
              <p className="note">
                There are no good clinical trials showing that coconut oil stops hair fall or makes hair grow. Be careful with any product that promises this.
              </p>
            </>
          ),
        },
        {
          id: "dandruff-hairfall",
          heading: "Does coconut oil help with dandruff or hair fall?",
          body: (
            <>
              <h3>Dandruff</h3>
              <p>
                We cannot tell you coconut oil cures dandruff, because the evidence is not there. Some people find their scalp feels less dry and tight after oiling. Others find that oil left on for a long time makes flaking worse. If you have a lot of flakes, an itchy or red scalp, or patches that do not go away, see a dermatologist rather than oiling more.
              </p>
              <h3>Hair fall</h3>
              <p>
                Everyone loses some hair every day. Sudden or heavy hair fall has many causes, from illness and stress to diet and hormones, and oil will not fix those. A doctor can find the reason. Champi can still be part of a gentle hair care routine, as long as you do not expect it to be a treatment.
              </p>
            </>
          ),
        },
        {
          id: "which-oil",
          heading: "Can you use cooking coconut oil on your hair?",
          body: (
            <>
              <p>
                Yes, if it is a plain, single ingredient coconut oil. Many hair oils sold in India have added perfume, colour or other oils. An edible cold pressed coconut oil has nothing added, so you know exactly what you are putting on your scalp.
              </p>
              <p>
                Karijeeva is one ingredient: coconut, grown on our family farms in Tiptur and on the Karnataka coast. It is not refined, bleached or deodorised, so it keeps its natural coconut smell. Every batch goes to an independent food testing lab, and our latest report is on the <Link to="/lab-reports">lab reports page</Link>. One jar can sit in the kitchen and the bathroom. If you would like to try it, <Link to="/products">see the sizes here</Link>.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        { q: "Is coconut oil good for hair?", a: "Many people find it leaves hair softer and easier to comb. Lab studies on hair fibres found coconut oil used before washing reduced protein loss (Rele and Mohile, 2003). Those were lab tests on hair samples, not trials on people." },
        { q: "Does coconut oil stop hair fall?", a: "There is no good clinical evidence that it does. Hair fall has many causes, so if it is sudden or heavy, see a doctor. Oiling can be part of gentle hair care, not a treatment." },
        { q: "Can coconut oil cure dandruff?", a: "We do not claim that, and the evidence is not there. Some people feel their scalp is less dry after oiling, while others find flaking gets worse. A persistent itchy or flaky scalp needs a dermatologist." },
        { q: "How long should I leave coconut oil in my hair?", a: "At least an hour before you wash. Many people leave it overnight with an old towel on the pillow. Wash with a mild shampoo, twice if needed." },
        { q: "Should I warm coconut oil before applying it to hair?", a: "Gently warming it makes it easier to spread, and it melts oil that has set solid in the cold. Stand the jar in warm water. It should feel warm on your wrist, never hot." },
        { q: "Can I use the same coconut oil for cooking and hair?", a: "Yes, if it is an edible, single ingredient coconut oil with an FSSAI licence number. Use a clean, dry spoon to take out oil for your hair so the jar stays clean for the kitchen." },
      ]}
      sources={[
        { label: "Rele and Mohile 2003, Effect of mineral oil, sunflower oil and coconut oil on prevention of hair damage, J Cosmet Sci", url: "https://www.researchgate.net/publication/10787534_Effect_of_mineral_oil_sunflower_oil_and_coconut_oil_on_prevention_of_hair_damage" },
        { label: "Rele and Mohile 2003, journal record with author affiliations, Society of Cosmetic Chemists library", url: "https://library.scconline.org/v054n02/99" },
        { label: "Ruetsch et al. 2001, oil penetration into hair, J Cosmet Sci (PubMed)", url: "https://pubmed.ncbi.nlm.nih.gov/11413497/" },
      ]}
      related={[
        { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
        { label: "Coconut oil for baby massage", to: "/coconut-oil-for-baby-massage" },
        { label: "Cold pressed vs refined coconut oil", to: "/cold-pressed-vs-refined-coconut-oil" },
        { label: "How to check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
      ]}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Skin                                                                */
/* ------------------------------------------------------------------ */

export function ForSkin() {
  return (
    <GuidePage
      path="/coconut-oil-for-skin"
      seoTitle="Coconut Oil for Skin: How to Use It Well"
      seoDescription="How to use coconut oil for dry skin, cracked heels and lips, what two clinical studies found and their limits, why to go easy on the face and how to patch test."
      eyebrow="Skin guide"
      title="Coconut oil for dry skin: how to use it, and where to go easy"
      tldr="Coconut oil works as a simple moisturiser for dry skin, heels, elbows and lips. Apply a few drops right after a bath while the skin is still damp. In a small 2004 trial, coconut oil did as well as mineral oil for dry skin. It can feel heavy on the face, so patch test first, and see a doctor for any skin condition."
      updated={UPDATED}
      testid="guide-skin"
      sections={[
        {
          id: "why",
          heading: "Why use coconut oil on skin?",
          body: (
            <>
              <p>
                In many Indian homes, the same jar of coconut oil does three jobs: cooking, hair and skin. A little on cracked heels before bed, on dry elbows in winter, or on the lips when they start to split. It is cheap, it is in the house, and it works as a plain moisturiser.
              </p>
              <p>
                Oils like coconut oil sit on the surface of the skin and slow down the loss of water. That is why they work best on skin that has just been wet, and why a small amount goes a long way.
              </p>
            </>
          ),
        },
        {
          id: "studies",
          heading: "What do studies say about coconut oil for dry skin?",
          body: (
            <>
              <p>
                Two clinical studies are worth knowing about. Both are real trials, and both have limits.
              </p>
              <h3>Dry skin in adults (Agero and Verallo Rowell, 2004)</h3>
              <p>
                A double blind trial in 34 people with dry skin (xerosis) compared extra virgin coconut oil with mineral oil over two weeks. Coconut oil was as effective and as safe as mineral oil for skin hydration and surface lipids. It was a small, short trial, and it showed coconut oil was on par with a basic moisturiser, not better than one.
              </p>
              <h3>Children with dry, itchy skin (Evangelista and colleagues, 2014)</h3>
              <p>
                A randomised, double blind trial in 117 children with mild to moderate atopic dermatitis compared virgin coconut oil with mineral oil over eight weeks. The coconut oil group did better on measures of severity, water loss through the skin and skin moisture. This was one well designed trial in the Philippines.
              </p>
              <p className="note">
                Both trials used virgin coconut oil. Karijeeva is cold pressed coconut oil, not virgin, so these results do not transfer directly to our oil or any other. And neither study means coconut oil treats eczema or any skin disease. If your child has eczema, follow your doctor's advice.
              </p>
            </>
          ),
        },
        {
          id: "how-to",
          heading: "How to use coconut oil on dry skin",
          body: (
            <>
              <p>
                The one rule that matters most: put it on damp skin. After a bath, pat yourself mostly dry with a towel, leaving the skin slightly damp, then rub a few drops between your palms and press it in.
              </p>
              <Table
                caption="How to use coconut oil on different parts of the body"
                head={["Where", "How", "When"]}
                rows={[
                  ["Arms, legs and body", "A few drops at a time on damp skin, spread thinly.", "Right after a bath"],
                  ["Cracked heels", "Soak feet in warm water, pat dry, rub in a thin layer, wear cotton socks.", "At bedtime"],
                  ["Elbows and knees", "A drop or two, massaged in.", "After a bath or before bed"],
                  ["Lips", "A tiny amount with a clean fingertip.", "Before bed and in dry weather"],
                  ["Hands and cuticles", "One drop rubbed into the cuticles and backs of the hands.", "After washing up"],
                ]}
              />
              <p>
                If your skin still feels greasy after ten minutes, you used too much. Next time, use half.
              </p>
            </>
          ),
        },
        {
          id: "face",
          heading: "Can you put coconut oil on your face?",
          body: (
            <>
              <p>
                Go carefully. Coconut oil is heavy, and many people with oily or acne prone skin find it too rich for the face. If you get pimples easily, we suggest you do not use it on your face at all, or try it only on a small area first.
              </p>
              <p>
                If your facial skin is very dry, a drop pressed onto damp skin at night may feel comfortable. Watch how your skin responds over a week or two, and stop if you see new breakouts or irritation.
              </p>
              <h3>How to patch test</h3>
              <ol>
                <li>Put a small drop on the inside of your forearm or behind the ear.</li>
                <li>Leave it for 24 hours without washing the area.</li>
                <li>If you see redness, itching, swelling or bumps, do not use it.</li>
              </ol>
            </>
          ),
        },
        {
          id: "when-not",
          heading: "When not to use coconut oil on skin",
          body: (
            <>
              <ul>
                <li>On broken skin, open cuts, burns or weeping rashes.</li>
                <li>If you know you are allergic to coconut.</li>
                <li>As a replacement for a treatment your doctor has prescribed.</li>
                <li>As a sunscreen. It is not one.</li>
              </ul>
              <p>
                For any rash, patch or itch that lasts, or anything that looks infected, see a doctor or dermatologist.
              </p>
            </>
          ),
        },
        {
          id: "which-oil",
          heading: "Which coconut oil should you use on skin?",
          body: (
            <>
              <p>
                For skin, the fewer ingredients the better. Choose a coconut oil with one ingredient and no added perfume, colour or other oils. Unrefined oil keeps its natural coconut smell, which most people find pleasant.
              </p>
              <p>
                Karijeeva is cold pressed from coconuts grown on our family farms in Tiptur and on the Karnataka coast, with nothing added. Every batch goes to an independent food testing lab, and we have tested {BRAND.batchesTested} batches so far. You can read our latest report on the <Link to="/lab-reports">lab reports page</Link>. If a single ingredient oil for your kitchen and your skin sounds right, <Link to="/products">see our jars here</Link>.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        { q: "Is coconut oil good for dry skin?", a: "It works as a simple moisturiser. In a small 2004 trial of 34 people with dry skin, coconut oil was as effective and safe as mineral oil over two weeks. Apply it to damp skin after a bath." },
        { q: "Can I use coconut oil on my face?", a: "Carefully. It can feel heavy, and people with oily or acne prone skin often find it too rich. Patch test first, and stop if you see breakouts or irritation." },
        { q: "Is coconut oil good for cracked heels?", a: "Many people use it that way. Soak your feet, pat them dry, rub in a thin layer at bedtime and wear cotton socks. Deep cracks that bleed or hurt need a doctor." },
        { q: "Can coconut oil treat eczema?", a: "No, we do not claim that. One trial in children with mild to moderate atopic dermatitis found virgin coconut oil did better than mineral oil as a moisturiser, but that does not make it a treatment. Follow your doctor's advice for eczema." },
        { q: "Should I apply coconut oil on wet or dry skin?", a: "Slightly damp skin is best. Oil slows the loss of water from the skin, so it works best right after a bath when the skin still holds some moisture." },
      ]}
      sources={[
        { label: "Agero and Verallo Rowell 2004, coconut oil vs mineral oil for xerosis, Dermatitis", url: "https://journals.sagepub.com/doi/abs/10.2310/derm.1.2004.2015" },
        { label: "Evangelista et al. 2014, virgin coconut oil in children with atopic dermatitis, Int J Dermatol (PubMed)", url: "https://pubmed.ncbi.nlm.nih.gov/24320105/" },
      ]}
      related={[
        { label: "Coconut oil for hair", to: "/coconut-oil-for-hair" },
        { label: "Coconut oil for baby massage", to: "/coconut-oil-for-baby-massage" },
        { label: "Cold pressed vs refined coconut oil", to: "/cold-pressed-vs-refined-coconut-oil" },
        { label: "Our lab report", to: "/lab-reports" },
      ]}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Baby massage                                                        */
/* ------------------------------------------------------------------ */

export function ForBabyMassage() {
  return (
    <GuidePage
      path="/coconut-oil-for-baby-massage"
      seoTitle="Coconut Oil for Baby Massage: A Guide"
      seoDescription="Using coconut oil for baby massage: the Indian tradition, what newborn trials looked at and their limits, how to patch test, how to massage, and what to avoid."
      eyebrow="Baby care guide"
      title="Coconut oil for baby massage: a calm, careful guide"
      tldr="Coconut oil has a long tradition in Indian baby massage, and it has been studied in Indian newborn trials in hospitals. Talk to your paediatrician before you start, especially if your baby was born early or has eczema. Choose a single ingredient oil, patch test for 24 hours, warm it slightly, and keep it away from the eyes, nose and broken skin."
      updated={UPDATED}
      testid="guide-baby-massage"
      sections={[
        {
          id: "tradition",
          heading: "Why do Indian families massage babies with oil?",
          body: (
            <>
              <p>
                In most Indian homes, the daily oil massage before a bath is simply part of having a baby. A grandmother, a mother or a trusted helper sits with the baby on her outstretched legs and works the oil in with slow, firm strokes. In Kerala, coastal Karnataka and much of the South, that oil has usually been coconut oil.
              </p>
              <p>
                Parents value the massage as a quiet time of touch and closeness, and many say their baby sleeps well after it. Those reasons do not need a study to be real. When it comes to what the oil itself does, though, it is worth knowing what has and has not been tested.
              </p>
            </>
          ),
        },
        {
          id: "studies",
          heading: "What have studies on coconut oil and newborns looked at?",
          body: (
            <>
              <h3>Indian Pediatrics, 2005 (Sankaranarayanan and colleagues)</h3>
              <p>
                This Indian trial compared massage with coconut oil, massage with mineral oil and a placebo powder in newborns, including babies born early. It measured how fast babies gained weight, and the coconut oil group gained weight faster than the comparison groups. It was an open trial, meaning the families and staff knew which oil was used, and it was run in a hospital setting.
              </p>
              <h3>Neonatology, 2018 (Strunk and colleagues)</h3>
              <p>
                This trial looked at babies born before 30 weeks of pregnancy. Coconut oil was applied to their skin under medical supervision for 21 days. Their skin condition scores were better than in the comparison group, and no side effects were reported in the trial.
              </p>
              <p className="note">
                These are useful studies, but they were done in hospitals with close supervision, and some involved very premature babies. They do not mean that massage oil at home will change your baby's weight or skin. If your baby was born early, has low birth weight or has any skin problem, ask your paediatrician before you use any oil.
              </p>
            </>
          ),
        },
        {
          id: "patch-test",
          heading: "How to do a patch test before the first massage",
          body: (
            <>
              <p>
                Even a pure, single ingredient oil can disagree with some babies. A patch test takes one day and gives you peace of mind.
              </p>
              <Table
                caption="How to patch test coconut oil on a baby"
                head={["Step", "What to do"]}
                rows={[
                  ["1", "Choose a small patch of healthy skin, such as the inside of the forearm or the thigh."],
                  ["2", "Rub in one or two drops of the oil."],
                  ["3", "Leave it for 24 hours. Do not wash that spot."],
                  ["4", "Check for redness, rash, bumps, dryness or your baby scratching the area."],
                  ["5", "If you see any reaction, do not use the oil, and show the spot to your paediatrician."],
                  ["6", "If the skin looks normal, you can start with a short massage and a small amount of oil."],
                ]}
              />
            </>
          ),
        },
        {
          id: "how-to",
          heading: "How to give your baby a coconut oil massage",
          body: (
            <>
              <ol>
                <li><strong>Pick a calm time.</strong> When your baby is awake and content, not hungry and not just fed. Many families massage before the morning bath.</li>
                <li><strong>Keep the room warm</strong> and free of draughts, and lay your baby on a soft towel.</li>
                <li><strong>Warm the oil slightly.</strong> Stand the jar in warm water for a few minutes. Test a drop on the inside of your wrist. It should feel barely warm. Never heat oil directly on the stove for a baby.</li>
                <li><strong>Use a little.</strong> About half a teaspoon to a teaspoon, rubbed between your palms, is enough to start. Add more only if your hands drag.</li>
                <li><strong>Use gentle, slow strokes.</strong> Legs and feet first, then arms and hands, then the tummy in slow clockwise circles, then chest and back.</li>
                <li><strong>Watch your baby.</strong> If they cry, stiffen or turn away, stop and try another day.</li>
                <li><strong>Hold on firmly afterwards.</strong> An oiled baby is slippery. Take extra care when you lift them into the bath.</li>
              </ol>
            </>
          ),
        },
        {
          id: "avoid",
          heading: "What should you avoid?",
          body: (
            <>
              <ul>
                <li><strong>Eyes, nose and ears.</strong> Do not put oil in them, even though some families traditionally do.</li>
                <li><strong>Broken or irritated skin,</strong> rashes, and the umbilical cord stump until it has fallen off and healed.</li>
                <li><strong>Oils with anything added,</strong> such as perfume, colour or blends of other oils.</li>
                <li><strong>Hot oil.</strong> A baby's skin burns more easily than yours.</li>
                <li><strong>Massaging a baby who is unwell,</strong> feverish or upset.</li>
              </ul>
              <p>
                Talk to your paediatrician first if your baby was born early, has eczema or very dry skin, has a family history of allergies, or has any skin condition. They know your baby and can tell you what suits them.
              </p>
            </>
          ),
        },
        {
          id: "safe",
          heading: "Is coconut oil safe for my baby?",
          body: (
            <>
              <p>
                No oil maker can promise that an oil is safe for every baby, and you should be wary of any who does. What you can do is choose an oil where you know exactly what is inside, patch test it, and follow your paediatrician's advice.
              </p>
              <p>
                That is where Karijeeva fits. It has one ingredient, coconut, grown on our family farms in Tiptur and on the Karnataka coast. It is not refined, bleached or deodorised, and nothing is added. Every batch is tested at an independent food testing lab, and our latest report is on the <Link to="/lab-reports">lab reports page</Link>. If that is the kind of oil you want near your baby, you can <Link to="/products">see our sizes here</Link>. Unopened jars can be returned within 7 days.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        { q: "Is coconut oil good for baby massage?", a: "It has a long tradition in Indian baby massage and has been studied in Indian newborn trials in hospitals. Talk to your paediatrician first, choose a single ingredient oil, and patch test it for 24 hours." },
        { q: "Can I use coconut oil for a newborn?", a: "Ask your paediatrician before you start, especially if your baby was born early or has low birth weight. Avoid the umbilical cord stump until it has healed, and use only a small amount." },
        { q: "Is coconut oil good for baby massage in winter?", a: "Many families use it year round. In cold weather it turns solid below about 24°C, so stand the jar in warm water to melt it and test the temperature on your wrist before use." },
        { q: "Can I put coconut oil in my baby's nose or ears?", a: "No. Keep oil away from the eyes, nose and ears, even though some families traditionally do this. If your baby has a blocked nose or an ear problem, ask your paediatrician." },
        { q: "My baby has eczema. Can I use coconut oil?", a: "Talk to your paediatrician first. One trial found virgin coconut oil worked well as a moisturiser in children with mild to moderate atopic dermatitis, but it is not a treatment, and your doctor may prefer a specific product." },
        { q: "How much oil should I use for a baby massage?", a: "Start with about half a teaspoon to a teaspoon, rubbed between your palms. You need only enough for your hands to glide. Too much makes the baby slippery and hard to hold." },
      ]}
      sources={[
        { label: "Sankaranarayanan et al. 2005, oil massage in neonates, Indian Pediatrics (PubMed)", url: "https://pubmed.ncbi.nlm.nih.gov/16208048/" },
        { label: "Indian Pediatrics, September 2005, full text", url: "https://www.indianpediatrics.net/sep2005/sep-877-884.htm" },
        { label: "Strunk et al. 2018, topical coconut oil in very preterm infants, Neonatology (PubMed)", url: "https://pubmed.ncbi.nlm.nih.gov/29197867/" },
        { label: "Evangelista et al. 2014, virgin coconut oil in children with atopic dermatitis, Int J Dermatol (PubMed)", url: "https://pubmed.ncbi.nlm.nih.gov/24320105/" },
      ]}
      related={[
        { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
        { label: "Coconut oil for hair", to: "/coconut-oil-for-hair" },
        { label: "How to check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
        { label: "Where our coconuts grow", to: "/the-farm" },
      ]}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Oil pulling                                                         */
/* ------------------------------------------------------------------ */

export function OilPulling() {
  return (
    <GuidePage
      path="/oil-pulling-with-coconut-oil"
      seoTitle="Oil Pulling With Coconut Oil: How to Do It"
      seoDescription="How to do oil pulling with coconut oil: gandusha and kavala, a step by step method, what small studies found, and why it never replaces brushing or a dentist."
      eyebrow="Daily ritual guide"
      title="Oil pulling with coconut oil: how to do it, and what to expect"
      tldr="Oil pulling means swishing a spoonful of oil around your mouth for 5 to 10 minutes, then spitting it out. It comes from the Ayurvedic practices of gandusha and kavala. Small studies have looked at coconut oil pulling for plaque and gum health, and the evidence is weak to moderate. It does not replace brushing, flossing or seeing a dentist."
      updated={UPDATED}
      testid="guide-oil-pulling"
      sections={[
        {
          id: "what",
          heading: "What is oil pulling?",
          body: (
            <>
              <p>
                Oil pulling is an old Ayurvedic habit. You take a spoonful of oil into your mouth, move it around your teeth and gums for several minutes, then spit it out. Traditional texts describe two forms. In gandusha, you hold the oil in the mouth without moving it. In kavala, you swish it around and then spit.
              </p>
              <p>
                Sesame oil is the oil traditionally named for it. Coconut oil has become popular because many people prefer its milder taste, and in South Indian homes it is already on the shelf.
              </p>
            </>
          ),
        },
        {
          id: "how-to",
          heading: "How to do oil pulling with coconut oil, step by step",
          body: (
            <>
              <Table
                caption="Oil pulling with coconut oil, step by step"
                head={["Step", "What to do", "Tip"]}
                rows={[
                  ["1", "In the morning, before you eat or drink, take 1 teaspoon to 1 tablespoon of coconut oil.", "Start with a teaspoon if you are new to it."],
                  ["2", "If it is solid, let it melt in your mouth for a few seconds.", "It melts quickly at body temperature."],
                  ["3", "Swish it gently around your mouth, pushing and pulling it between the teeth.", "Keep your jaw relaxed. Slow is fine."],
                  ["4", "Keep going for 5 to 10 minutes.", "Start with 5 minutes and build up. Do it while you get ready."],
                  ["5", "Spit the oil into a tissue or paper and throw it in the dustbin.", "Not in the sink. Coconut oil sets solid and can block drains."],
                  ["6", "Rinse your mouth with warm water.", "Some people rinse with warm salt water."],
                  ["7", "Brush your teeth as usual.", "Oil pulling comes before brushing, never instead of it."],
                ]}
              />
              <p className="note">
                Do not swallow the oil. It has been through your mouth for ten minutes and you want it out, not in.
              </p>
            </>
          ),
        },
        {
          id: "evidence",
          heading: "Does oil pulling with coconut oil work?",
          body: (
            <>
              <p>
                The honest answer: there is some early evidence, but not enough to be sure. Here is what has been studied.
              </p>
              <ul>
                <li><strong>Peedikayil and colleagues, 2015.</strong> A small, preliminary study found coconut oil pulling was linked to lower plaque and gingivitis scores.</li>
                <li><strong>Woolley and colleagues, 2020.</strong> A systematic review in Heliyon found coconut oil pulling showed some effect on plaque index scores.</li>
                <li><strong>Gbinigie and colleagues, 2016.</strong> A review of oil pulling found five small trials, all in India and all using sesame oil, with varying risk of bias. An Oxford commentary on it summed up the evidence as: anecdotally yes, but the jury is still out.</li>
              </ul>
              <p>
                Taken together, the evidence is weak to moderate. The studies are small, often short, and hard to blind, since everyone knows whether they are swishing oil. That is why we describe oil pulling as a traditional habit some people like, not a treatment.
              </p>
            </>
          ),
        },
        {
          id: "not-replace",
          heading: "What oil pulling does not do",
          body: (
            <>
              <ul>
                <li>It does not replace brushing twice a day with a fluoride toothpaste.</li>
                <li>It does not replace flossing or cleaning between your teeth.</li>
                <li>It does not treat cavities, gum disease, tooth pain or bad breath that will not go away.</li>
                <li>It does not replace regular visits to a dentist.</li>
              </ul>
              <p>
                If your gums bleed, a tooth hurts, or bad breath does not go away, see a dentist. Oil pulling will not fix those things, and waiting can make them worse.
              </p>
            </>
          ),
        },
        {
          id: "who",
          heading: "Who should not do oil pulling?",
          body: (
            <>
              <ul>
                <li><strong>Young children,</strong> who may swallow the oil or choke on it.</li>
                <li><strong>Anyone who has trouble swallowing</strong> or who gags easily.</li>
                <li><strong>People allergic to coconut.</strong></li>
                <li><strong>Anyone with jaw pain.</strong> Ten minutes of swishing can make a sore jaw worse. Try shorter sessions or skip it.</li>
              </ul>
              <p>
                If you are unsure whether it suits you, ask your dentist at your next check up.
              </p>
            </>
          ),
        },
        {
          id: "which-oil",
          heading: "Which coconut oil should you use for oil pulling?",
          body: (
            <>
              <p>
                You are putting this oil in your mouth every day, so choose an edible coconut oil with an FSSAI licence number, one ingredient and nothing added. Unrefined oil tastes gently of coconut, which most people find easier than a flavourless oil.
              </p>
              <p>
                Karijeeva is cold pressed from coconuts grown on our family farms in Tiptur and on the Karnataka coast. It is not refined, bleached or deodorised, and every batch is tested at an independent food testing lab. Our FSSAI licence is {BRAND.fssai}. The same jar you use for oil pulling works in your tadka. If you would like to try it, <Link to="/products">see our jars here</Link>, and if you want to check the oil before you trust it, our guide on <Link to="/how-to-check-coconut-oil-purity">how to check coconut oil purity</Link> shows you how.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        { q: "Does oil pulling with coconut oil really work?", a: "Small studies have linked coconut oil pulling to lower plaque and gingivitis scores, and a 2020 review found some effect on plaque. The evidence is weak to moderate. It is a traditional habit, not a replacement for brushing or a dentist." },
        { q: "How long should I do oil pulling?", a: "Five to ten minutes is common. Start with five minutes and build up. Then spit the oil into the dustbin, rinse, and brush as usual." },
        { q: "Should I do oil pulling before or after brushing?", a: "Most people do it first thing in the morning before brushing, then brush afterwards. Either way, keep brushing twice a day with a fluoride toothpaste." },
        { q: "Can I swallow the oil after oil pulling?", a: "No. Spit it out into a tissue or the dustbin. Do not spit it into the sink, as coconut oil sets solid and can block drains." },
        { q: "Which oil is best for oil pulling, coconut or sesame?", a: "Sesame is the oil named in traditional texts and used in most of the older Indian studies. Coconut oil has been studied more recently and many people prefer its taste. Pick the edible oil you are comfortable putting in your mouth daily." },
      ]}
      sources={[
        { label: "Peedikayil et al. 2015, coconut oil pulling and plaque related gingivitis, Niger Med J", url: "https://dx.doi.org/10.4103/0300-1652.153406" },
        { label: "Woolley et al. 2020, systematic review of oil pulling, Heliyon", url: "https://www.sciencedirect.com/science/article/pii/S2405844020316327" },
        { label: "CEBM Oxford commentary on Gbinigie et al. 2016, oil pulling review", url: "https://www.cebm.ox.ac.uk/news/views/oil-pulling-does-it-work-anecdotally-yes-but-the-jury-is-still-out-in-this-review" },
      ]}
      related={[
        { label: "Coconut oil for hair", to: "/coconut-oil-for-hair" },
        { label: "Cooking with coconut oil", to: "/cooking-with-coconut-oil" },
        { label: "How to check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
        { label: "Cold pressed coconut oil benefits", to: "/cold-pressed-coconut-oil-benefits" },
      ]}
    />
  );
}
